import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Waves, Crown, CalendarCheck, Gift, BadgePercent } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { PageHero, SectionHead, Field, Input, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import { submitLead, formatApiError } from "@/lib/api";

const TIERS = [
  {
    icon: Waves,
    name: "Co-Host Island",
    unlock: "Automatic after your 2nd booking",
    perks: ["10% off all guest services", "Stayover refreshes & full cleans at member rates", "Priority dates in high season", "Member-only seasonal offers"],
    featured: false,
  },
  {
    icon: Crown,
    name: "Co-Host Gold",
    unlock: "Automatic after your 4th booking",
    perks: ["15% off all guest services", "Everything in Island", "First access to seasonal offers", "Complimentary welcome setup with arrival cleans"],
    featured: true,
  },
];

const STEPS = [
  ["Book", "Stay at a property we care for — or book our guest services while you're here."],
  ["Stay Again", "Your second booking is when the welcome happens: you're enrolled automatically. No cards, no points, no apps."],
  ["Belong", "Every service you book from then on carries your member discount — our way of saying welcome back."],
];

export default function Loyalty() {
  usePageMeta("The Co-Host Circle", "Returning guests belong here. Automatic membership after your second booking — discounts on guest services across Bermuda.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", last_property: "", last_stay: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("loyalty_signup", form);
      setDone(true);
      toast.success("Welcome to the Circle — we'll confirm your membership.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <PageHero
        overline="The Co-Host Circle"
        lines={["Stay Twice.", "Belong."]}
        copy="Guests who return to Bermuda deserve more than a thank-you email. After your second booking you're automatically enrolled in the Co-Host Circle — with real discounts on every service we offer, every visit."
        image={IMAGES.moonGate}
      >
        <a href="#join-circle" data-testid="loyalty-hero-join-btn" className="inline-flex items-center gap-2.5 bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory">
          Join the Circle
        </a>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="Membership"
          title="Two tiers. Zero paperwork."
          copy="Membership is automatic — tied to your bookings, not a signup form. The form below is simply how to reach us if you think you're already eligible or want your stays counted."
        />
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1} className="h-full">
              <div data-testid={`loyalty-tier-${t.name.toLowerCase().replace(/\s/g, "-")}`} className={`flex h-full flex-col p-9 lg:p-12 ${t.featured ? "bg-navy text-ivory shadow-2xl shadow-navy/25" : "border border-navy/10 bg-white"}`}>
                <span className={`flex h-11 w-11 items-center justify-center rounded-full ${t.featured ? "bg-bermuda/20 text-bermuda" : "bg-pink-soft text-bermuda-dark"}`}>
                  <t.icon size={18} strokeWidth={1.5} />
                </span>
                <h3 className={`mt-6 font-serif text-3xl ${t.featured ? "text-ivory" : "text-navy"}`}>{t.name}</h3>
                <p className={`mt-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] ${t.featured ? "text-bermuda" : "text-bermuda-dark"}`}>{t.unlock}</p>
                <ul className="mt-7 flex-1 space-y-3">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm">
                      <BadgePercent size={15} className={`mt-0.5 shrink-0 ${t.featured ? "text-bermuda" : "text-ocean"}`} />
                      <span className={t.featured ? "text-ivory/85" : "text-navy/75"}>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-10 sm:grid-cols-3">
          {STEPS.map(([title, copy], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div>
                <span className="font-serif text-5xl font-light text-bermuda/50">{String(i + 1).padStart(2, "0")}</span>
                <h4 className="mt-3 flex items-center gap-2.5 font-serif text-xl text-navy">
                  {i === 0 && <CalendarCheck size={16} className="text-bermuda-dark" />}
                  {i === 1 && <Gift size={16} className="text-bermuda-dark" />}
                  {i === 2 && <Waves size={16} className="text-bermuda-dark" />}
                  {title}
                </h4>
                <p className="mt-2 text-[13px] leading-relaxed text-navy/60">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="join-circle" className="scroll-mt-24 bg-sand-light">
        <div className="mx-auto max-w-[900px] px-6 py-24 lg:px-10">
          <SectionHead
            overline="Claim Your Membership"
            title={<>Already stayed twice?<br /><span className="italic text-bermuda-dark">You're probably in.</span></>}
            copy="Tell us where you stayed and we'll confirm your membership against our records — or register your interest for your next visit."
          />
          <Reveal delay={0.15}>
            <div className="mt-12 border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12">
              {done ? (
                <div className="py-8 text-center" data-testid="loyalty-success">
                  <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda-dark" />
                  <h3 className="mt-6 font-serif text-3xl text-navy">Welcome back, officially.</h3>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-navy/60">
                    We'll confirm your Circle membership personally — and your member discount will be waiting for your next Bermuda stay.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="loyalty-form">
                  <Field label="Your Name" required><Input data-testid="loyalty-name" required value={form.name} onChange={set("name")} /></Field>
                  <Field label="Email" required><Input data-testid="loyalty-email" required type="email" value={form.email} onChange={set("email")} /></Field>
                  <Field label="Phone / WhatsApp"><Input data-testid="loyalty-phone" type="tel" value={form.phone} onChange={set("phone")} /></Field>
                  <Field label="Property You Last Stayed At"><Input data-testid="loyalty-property" value={form.last_property} onChange={set("last_property")} placeholder="Property name or area" /></Field>
                  <Field label="Approximate Date of Last Stay"><Input data-testid="loyalty-last-stay" value={form.last_stay} onChange={set("last_stay")} placeholder="e.g. June 2026" /></Field>
                  <Field label="Number of Stays With Us">
                    <Input data-testid="loyalty-stays" value={form.stays || ""} onChange={set("stays")} placeholder="e.g. 2" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Anything Else?"><Textarea data-testid="loyalty-message" value={form.message} onChange={set("message")} placeholder="Upcoming trip dates, questions about member rates…" /></Field>
                  </div>
                  <div className="sm:col-span-2">
                    <SubmitButton loading={loading} testid="loyalty-form-submit">Join the Co-Host Circle</SubmitButton>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-center text-xs leading-relaxed text-navy/45">
              Member discounts apply to The Bermuda Co-Host guest services — stayover refreshes, full cleans and add-ons.
              Membership is confirmed against booking records and activated automatically after your second booking.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
